/* eslint-disable @typescript-eslint/no-explicit-any */
import { KalturaHouseholdDevice } from '@kaltura-ott/ott-client-types';
import { KuxDms } from '@kaltura-ott/tvpil';
import {
  eventTypes,
  KUxAnalytics,
  KUxAnalyticsPlugin,
  KUxBaseEventData,
  pluginsMap,
} from '@kaltura-ott/tvpil-analytics';
import { observable } from 'mobx';
import { container } from 'tsyringe';

import type { AppParams, UserStore } from '../stores';
import type { LoginStore } from '../stores/user/login-store';

const ANALYTICS_API_VERSION = '5.0.3';

export class Analytics {
  @observable isReady = false;
  @observable hasUser = false;

  private kuxAnalytics?: KUxAnalytics;
  private baseEventData?: KUxBaseEventData;
  private queue: any = [];
  private isWeb = false;

  init(params: AppParams, dms: KuxDms, ks: string) {
    // create base event data
    const plugins: Array<KUxAnalyticsPlugin> = [];
    dms.analytics.plugins?.forEach((plugin) => {
      const pluginName = Object.keys(plugin)[0];
      plugins.push(pluginsMap[pluginName](plugin[pluginName].config) as KUxAnalyticsPlugin);
    });
    this.isWeb = params?.dmsSetting?.platform === 'other';

    this.baseEventData = {
      groupId: params.dmsSetting.partnerId,
      userId: 0,
      udid: params.udid,
      ks,
      hhId: 0,
      deviceFamily: String(dms?.deviceFamilyId),
      deviceBrand: String(params.deviceAttributes.brandId),
      osName: params.deviceAttributes.osName,
      deviceManufacturer: params.deviceAttributes.deviceManufacturer,
      deviceModel: params.deviceAttributes.deviceModel,
      firmware:
        params?.dmsSetting?.platform === 'stb' && params.deviceAttributes?.firmware
          ? params.deviceAttributes.firmware
          : undefined,
      apiVersion: ANALYTICS_API_VERSION,
    };

    if (!this.isWeb) {
      this.baseEventData.osVersion = params.deviceAttributes.osVersion;
    }

    // if externalKey defined in DMS config initiate it
    if (dms.analytics.externalKey) {
      this.baseEventData.externalKey = dms.analytics.externalKey;
    }

    // Initialize and configure KUxAnalytics
    // At least one plugin should be used
    KUxAnalytics.configure(
      {
        app: params.dmsSetting.applicationName,
        version: params.dmsSetting.clientVersion,
        plugins,
      },
      this.baseEventData,
    );
    this.kuxAnalytics = KUxAnalytics.getInstance();
    this.isReady = true;

    this.processQueue();
  }

  updateUser(userStore: UserStore, householdDevice: KalturaHouseholdDevice) {
    const loginStore = container.resolve<LoginStore>('loginStore');
    const baseEventData: KUxBaseEventData = {
      ...(this.baseEventData as KUxBaseEventData),
      userId: Number(userStore.userData?.id),
      ks: String(loginStore?.ks),
      hhId: householdDevice?.householdId,
    };

    Object.assign(this.baseEventData as KUxBaseEventData, baseEventData);

    if (this.baseEventData) {
      this.kuxAnalytics?.updateBaseData(this.baseEventData);
    }
    this.hasUser = true;
  }

  private async processQueue() {
    while (this.queue.length) {
      const { event, callback, errCallback } = this.queue.shift();
      this.send(event, callback, errCallback);
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public send(event: eventTypes, callback?: (...params: any[]) => any, errCallback?: Function) {
    if (this.isReady && this.kuxAnalytics) {
      this.kuxAnalytics.send(event, callback);
    } else {
      this.queue.push({ event, callback, errCallback });
    }
  }

  public reset() {
    this.queue = [];
    this.isReady = false;
    this.kuxAnalytics = undefined;
  }
}
